import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { AlertCircle, PhoneCall, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { denseTable, EmptyValue, MetricGrid, Section, StateBlock, StatusBadge } from "@/components/layout-2/ds";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import type { BadgeValue } from "@/types/dynamic-detail";
import { formatParis, humanizeToken, utcTooltip } from "@/utils/dialer-datetime";
import { formatNumber } from "@/utils/format-fr";

const LIVE_POLL_MS = 5_000;

export type CampaignLiveCardRow = {
    id: number | null;
    label: string;
    url: string | null;
    state: string | null;
    current_node_id: string | null;
    awaiting_kind: string | null;
    priority: number | null;
    wake_at: string | null;
    eta_seconds: number | null;
    phase?: "composing" | "in_call" | null;
    /** `phase` as served by `campaign_live.phase_badge`: render it, never recolor it. */
    phase_badge?: BadgeValue | null;
};

export type CampaignLiveContent = {
    as_of: string;
    counts: {
        active: number;
        due_now: number;
        in_call: number;
        composing: number;
        waiting_timer: number;
        waiting_reply: number;
        waiting_ai: number;
    };
    upcoming: CampaignLiveCardRow[];
    imminent: CampaignLiveCardRow[];
};

function formatEta(seconds: number | null): string {
    if (seconds === null) return "—";
    if (seconds <= 0) return "Due now";
    const absolute = Math.abs(seconds);
    if (absolute < 60) return `in ${absolute}s`;
    const minutes = Math.floor(absolute / 60);
    const remain = absolute % 60;
    if (minutes < 60) return remain ? `in ${minutes}m ${remain}s` : `in ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins ? `in ${hours}h ${mins}m` : `in ${hours}h`;
}

function CardLink({ row }: { row: CampaignLiveCardRow }) {
    if (row.url && row.id !== null) {
        return (
            <NavLink to={row.url} className="text-blue-700 hover:text-blue-900 hover:underline">
                {row.label}
            </NavLink>
        );
    }
    return <span>{row.label}</span>;
}

export function CampaignLiveSection({ campaignId, initial }: { campaignId: number; initial: CampaignLiveContent }) {
    const [content, setContent] = useState(initial);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    // Requests are numbered so that a slow answer never overwrites a fresher one.
    const issued = useRef(0);
    const applied = useRef(0);
    const mounted = useRef(true);

    useEffect(() => {
        setContent(initial);
    }, [initial]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const load = useCallback(async () => {
        const seq = ++issued.current;
        setRefreshing(true);
        try {
            const next = (await DataService.client.url(`/crm/objects/dialer-campaigns/${campaignId}/live/`).get().json()) as CampaignLiveContent;
            if (!mounted.current || seq !== issued.current || seq <= applied.current) return;
            applied.current = seq;
            setContent(next);
            setError(null);
        } catch {
            if (mounted.current && seq === issued.current) {
                setError("Live campaign queue could not be refreshed.");
            }
        } finally {
            if (mounted.current && seq === issued.current) setRefreshing(false);
        }
    }, [campaignId]);

    useEffect(() => {
        let cancelled = false;
        let timer: number | undefined;
        // Answers still in flight describe the previous campaign, so retire them.
        applied.current = issued.current;

        const schedule = () => {
            window.clearTimeout(timer);
            if (cancelled || document.visibilityState !== "visible") return;
            timer = window.setTimeout(() => {
                if (cancelled || document.visibilityState !== "visible") return;
                void load().finally(schedule);
            }, LIVE_POLL_MS);
        };

        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                void load().finally(schedule);
            } else {
                window.clearTimeout(timer);
            }
        };

        schedule();
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [campaignId, load]);

    const { counts } = content;

    return (
        <Section
            icon="Activity"
            title="Live queue"
            actions={
                <>
                    <span className="text-xs text-gray-500" title={utcTooltip(content.as_of)}>
                        As of {formatParis(content.as_of, "HH:mm:ss")}
                        {refreshing ? " · refreshing…" : ""}
                    </span>
                    <Button size="sm" variant="outline" className="h-7 bg-white" onClick={() => void load()} disabled={refreshing}>
                        <RefreshCw className={cn("mr-1 h-4 w-4", refreshing && "animate-spin")} /> Refresh
                    </Button>
                </>
            }
        >
            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Live queue unavailable</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <MetricGrid
                columns={4}
                metrics={[
                    { label: "In call", value: formatNumber(counts.in_call), hint: "Conversation attached" },
                    { label: "Composing", value: formatNumber(counts.composing), hint: "Waiting to place / outbox" },
                    { label: "Due now", value: formatNumber(counts.due_now), hint: "Ready for the dispatcher" },
                    { label: "Active", value: formatNumber(counts.active), hint: "Still in the engine" }
                ]}
            />
            <MetricGrid
                columns={3}
                metrics={[
                    { label: "Waiting · timer", value: formatNumber(counts.waiting_timer) },
                    { label: "Waiting · reply", value: formatNumber(counts.waiting_reply) },
                    { label: "Waiting · AI", value: formatNumber(counts.waiting_ai) }
                ]}
            />

            <p className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                <PhoneCall className="h-4 w-4" /> Imminent calls
            </p>
            {content.imminent.length === 0 ? (
                <StateBlock message="No card is composing or in call right now." className="py-4" />
            ) : (
                <Table>
                    <TableHeader className={denseTable.header}>
                        <TableRow className={denseTable.headerRow}>
                            <TableHead className={denseTable.head}>Card</TableHead>
                            <TableHead className={denseTable.head}>Phase</TableHead>
                            <TableHead className={denseTable.head}>Node</TableHead>
                            <TableHead className={denseTable.head}>ETA</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {content.imminent.map((row, index) => (
                            <TableRow key={row.id ?? `imminent-${index}`}>
                                <TableCell className={denseTable.cell}>
                                    <CardLink row={row} />
                                </TableCell>
                                <TableCell className={denseTable.cell}>
                                    <StatusBadge value={row.phase_badge} />
                                </TableCell>
                                <TableCell className={cn(denseTable.cell, "font-mono text-xs")}>{row.current_node_id ?? <EmptyValue />}</TableCell>
                                <TableCell className={cn(denseTable.cell, "tabular-nums")} title={row.wake_at ? utcTooltip(row.wake_at) : undefined}>
                                    {formatEta(row.eta_seconds)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <p className="mt-2 text-xs text-gray-600">Next cards in the dispatcher queue</p>
            {content.upcoming.length === 0 ? (
                <StateBlock message="No active cards on this campaign." className="py-4" />
            ) : (
                <Table>
                    <TableHeader className={denseTable.header}>
                        <TableRow className={denseTable.headerRow}>
                            <TableHead className={denseTable.head}>Card</TableHead>
                            <TableHead className={denseTable.head}>Awaiting</TableHead>
                            <TableHead className={denseTable.head}>Node</TableHead>
                            <TableHead className={cn(denseTable.head, "text-right")}>Priority</TableHead>
                            <TableHead className={denseTable.head}>Next action</TableHead>
                            <TableHead className={denseTable.head}>ETA</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {content.upcoming.map((row, index) => (
                            <TableRow key={row.id ?? `upcoming-${index}`}>
                                <TableCell className={denseTable.cell}>
                                    <CardLink row={row} />
                                </TableCell>
                                <TableCell className={denseTable.cell}>{row.awaiting_kind ? <Badge tone="neutral">{humanizeToken(row.awaiting_kind)}</Badge> : <EmptyValue />}</TableCell>
                                <TableCell className={cn(denseTable.cell, "font-mono text-xs")}>{row.current_node_id ?? <EmptyValue />}</TableCell>
                                <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{row.priority ?? <EmptyValue />}</TableCell>
                                <TableCell className={cn(denseTable.cell, "tabular-nums")} title={row.wake_at ? utcTooltip(row.wake_at) : undefined}>
                                    {formatParis(row.wake_at)}
                                </TableCell>
                                <TableCell className={cn(denseTable.cell, "tabular-nums")}>{formatEta(row.eta_seconds)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Section>
    );
}
